import { getJsBaseURL } from "tools/env";
import { sentryCapture } from "sentry/sentry";
import { setStoragedJsVersion } from "./storageJsVersion";

const JS_STORE_NAME = "jsStorage";
const JS_DB_NAME = "jsDB";
const JS_SCRIPT_KEY = "js";

/**
 * Opens (or creates) the jsDB database and guarantees that the `jsStorage` object store exists.
 * If the store is missing in an existing database, it performs a version upgrade to create it.
 */
const openDatabase = async (): Promise<IDBDatabase> => {
  if (!("indexedDB" in window)) throw new Error("IndexedDB not available");

  const closeJsDB = () => {
    if (window.jsDB) {
      window.jsDB.close();
      window.jsDB = undefined;
    }
  };

  // Helper: open database & ensure store during upgrade.
  const open = (): Promise<IDBDatabase> =>
    new Promise((resolve, reject) => {
      const req = indexedDB.open(JS_DB_NAME, 1);

      req.onblocked = () => reject(new Error("IndexedDB blocked (another tab holds it open)"));
      req.onerror = () => reject(req.error ?? new Error("Failed to open IndexedDB"));

      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(JS_STORE_NAME)) {
          db.createObjectStore(JS_STORE_NAME, { keyPath: "name" });
        }
      };

      req.onsuccess = () => resolve(req.result);
    });

  closeJsDB();
  window.jsDB = await open();
  if (window.jsDB.objectStoreNames.contains(JS_STORE_NAME)) return window.jsDB; // most of the time we're good

  // Store missing ➜ delete DB & retry (one-off)
  console.warn("[jsUpdater] `jsStorage` store missing. Recreating database.");
  closeJsDB();

  await new Promise<void>((resolve, reject) => {
    const del = indexedDB.deleteDatabase(JS_DB_NAME);
    del.onblocked = () => reject(new Error("IndexedDB deletion blocked"));
    del.onerror = () => reject(del.error ?? new Error("IndexedDB deletion failed"));
    del.onsuccess = () => resolve();
  });

  // retry
  window.jsDB = await open();
  if (!window.jsDB.objectStoreNames.contains(JS_STORE_NAME)) {
    closeJsDB();
    throw new Error("`jsStorage` store still missing after DB recreation");
  }

  return window.jsDB;
};

const performStoreOperation = <T>(
  mode: IDBTransactionMode,
  storeOperation: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> => {
  return new Promise(async (resolve, reject) => {
    try {
      const db = await openDatabase();
      const transaction = db.transaction([JS_STORE_NAME], mode);
      const store = transaction.objectStore(JS_STORE_NAME);
      const request = storeOperation(store);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(new Error("Store operation failed"));
    } catch (error) {
      console.error("Database operation failed", error);
      reject(new Error(`Database operation failed: ${error}`));
    }
  });
};

export const updateJsStorage = (js: string, version: string): Promise<IDBValidKey> => {
  return performStoreOperation("readwrite", (store) => store.put({ name: JS_SCRIPT_KEY, version: version, js: js }));
};

export const downloadJsVersion = async (version: string, signal: AbortSignal): Promise<string> => {
  const scriptUrl = getJsBaseURL() + "?v=" + version;
  console.log("DownloadAndStoreJsVersion: Fetching from", scriptUrl);
  const response = await fetch(scriptUrl, {
    signal,
    headers: { "Cache-Control": "no-cache" }
  });

  if (!response.ok) {
    throw new Error(`DownloadAndStoreJsVersion: Fetch failed with status: ${response.status}`);
  }

  const js = await response.text();
  if (!js) {
    throw new Error("DownloadAndStoreJsVersion: Empty response content");
  }

  return js;
};

export const deleteJsStorage = (): Promise<void> => {
  return performStoreOperation("readwrite", (store) => store.put({ name: JS_SCRIPT_KEY, js: undefined }))
    .then(() => {
      setStoragedJsVersion("");
    })
    .catch((error) => {
      console.error("DeleteJsStorage: Failed to delete JS", error);
      sentryCapture("deleteJsStorage failed", { error }); // maybe we can remove in future, but for now it's useful
    });
};
